<template>
  <!-- Sam's side of "What do Sam's stars say about us?": the link in his
       message (?birthday_request=<token>) opens this one screen. The token is
       the permission; answering once reads both charts and tells them both. -->
  <Teleport to="body">
    <div class="br-overlay" data-testid="birthday-request">
      <div class="br">
        <template v-if="state === 'loading'"><p class="br-dim">One moment…</p></template>
        <template v-else-if="state === 'gone'">
          <h3 class="br-title">This link has gone quiet</h3>
          <p class="br-text">It may have been answered already. Your messages will have the reading.</p>
          <button type="button" class="br-btn" @click="$emit('close')">Close</button>
        </template>
        <template v-else-if="state === 'done'">
          <h3 class="br-title" data-testid="birthday-request-done">Thank you</h3>
          <p v-if="sunSign" class="br-sign" data-testid="birthday-request-sign">You’re {{ article(sunSign) }} {{ sunSign }}.</p>
          <p class="br-text">{{ info.requester_name }} will hear that your stars are in. You'll find what they say about
            you two in your messages.</p>
          <button type="button" class="br-btn" @click="$emit('close')">Close</button>
        </template>
        <template v-else>
          <BirthMoment mode="inline"
            :title="`${info.requester_name} wants to see what your stars say about you two`"
            subtitle="Your birthday is all it takes. A time and place show more."
            :optional="['time', 'place']" :with-name="info.requester_name"
            :saving="saving" :server-errors="errors" submit-label="Share my stars" @save="answer" />
          <button type="button" class="br-later" @click="$emit('close')">Not now</button>
        </template>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { onMounted, ref } from 'vue'
import BirthMoment from '@/components/widgets/BirthMoment.vue'
import { getBirthdayRequest, answerBirthdayRequest } from '@/api-service'
import { article, sunSignOfDate } from '@/utils/birth-ask'

const props = defineProps({ token: { type: String, required: true } })
defineEmits(['close'])

const state = ref('loading')
const info = ref({ requester_name: 'Someone' })
const saving = ref(false)
const errors = ref(null)
// The answer carries no signs: the Sun from the date, never on a cusp day.
const sunSign = ref('')

onMounted(async () => {
  try {
    info.value = await getBirthdayRequest(props.token)
    state.value = info.value.answered ? 'gone' : 'ask'
  } catch (e) {
    state.value = 'gone'
  }
})

async function answer(out) {
  saving.value = true; errors.value = null
  try {
    await answerBirthdayRequest(props.token, { birthdate: out.birthdate, time: out.time || null, ...(out.place ? { place: out.place } : {}) })
    sunSign.value = sunSignOfDate(out.birthdate)
    state.value = 'done'
  } catch (e) {
    const r = e && e.response
    if (r && r.status === 410) state.value = 'gone'
    else errors.value = (r && r.data && r.data.errors) || { _: 'That did not save. Try again.' }
  } finally { saving.value = false }
}
</script>

<style scoped>
.br-overlay { position: fixed; inset: 0; z-index: 1400; background: rgba(6,0,14,0.86); display: flex;
  align-items: center; justify-content: center; padding: 16px 12px; overflow-y: auto; }
.br { width: 100%; max-width: 520px; max-height: calc(100vh - 32px); overflow-y: auto; color: #e9dcff; }
.br-title { font-family: 'Cinzel', serif; color: #f5e8c0; margin: 0 0 8px; font-size: 18px; }
.br-text { margin: 0 0 14px; line-height: 1.5; }
.br-dim { color: #9b86c9; }
.br-sign { margin: 0 0 10px; font-family: 'Cinzel', serif; color: #f0d9a3; font-size: 16px; }
.br-btn { background: linear-gradient(180deg, #7b4bb0, #4e2776); color: #fff; border: none; border-radius: 999px;
  padding: 10px 22px; min-height: 44px; font-weight: 700; cursor: pointer; font-family: inherit; }
.br-later { display: block; margin: 12px auto 0; background: none; border: none; color: #9b86c9; text-decoration: underline;
  min-height: 44px; cursor: pointer; font-family: inherit; }
</style>
